import { getConfig } from '@superbet-group/web.lib.config';
import {
    IsoLanguageCode,
    ShopsRoutes,
    Country,
} from '@superbet-group/web.types';

import {
    AboutSuperbet,
    AboutSuperbetLink,
} from '@modules/layout/footer/types';

import { WikiPath } from '@modules/wiki/WikiPath';

const links = getAllAboutSuperbetLinks();

export const aboutSuperbetLinks: AboutSuperbetLink[] = {
    [Country.Ro]: [
        links[AboutSuperbet.AboutUs],
        links[AboutSuperbet.Agencies],
        links[AboutSuperbet.Help],
        links[AboutSuperbet.DataProtection],
        links[AboutSuperbet.CookiePolicy],
        links[AboutSuperbet.ResponsibleGaming],
        links[AboutSuperbet.AntiLaunderingPolicy],
        links[AboutSuperbet.GameRules],
        links[AboutSuperbet.TermsAndConditions],
        links[AboutSuperbet.License],
        links[AboutSuperbet.AuthorizationDecision],
    ],
    [Country.RoNapoleon]: [
        links[AboutSuperbet.AboutUs],
        links[AboutSuperbet.Agencies],
        links[AboutSuperbet.Help],
        links[AboutSuperbet.DataProtection],
        links[AboutSuperbet.CookiePolicy],
        links[AboutSuperbet.ResponsibleGaming],
        links[AboutSuperbet.AntiLaunderingPolicy],
        links[AboutSuperbet.GameRules],
        links[AboutSuperbet.TermsAndConditions],
        links[AboutSuperbet.License],
        links[AboutSuperbet.AuthorizationDecision],
    ],
    [Country.Pl]: [
        links[AboutSuperbet.AboutUs],
        links[AboutSuperbet.Careers],
        links[AboutSuperbet.PressOffice],
        links[AboutSuperbet.PrivacyPolicy],
        links[AboutSuperbet.ResponsibleGaming],
        links[AboutSuperbet.TermsAndConditions],
        links[AboutSuperbet.Help],
        links[AboutSuperbet.CookiePolicy],
    ],
    [Country.Com]: [
        links[AboutSuperbet.ResponsibleGaming],
        links[AboutSuperbet.PrivacyPolicy],
        links[AboutSuperbet.CookiePolicy],
        links[AboutSuperbet.TermsAndConditions],
        links[AboutSuperbet.BettingAndGameRules],
    ],
    [Country.Br]: [
        links[AboutSuperbet.Help],
        links[AboutSuperbet.ResponsibleGaming],
        links[AboutSuperbet.PrivacyPolicy],
        links[AboutSuperbet.CookiePolicy],
        links[AboutSuperbet.TermsAndConditions],
        links[AboutSuperbet.BettingAndGameRules],
        links[AboutSuperbet.LicenseBrazil],
        links[AboutSuperbet.CustomerSupport],
    ],
    [Country.Rs]: [
        links[AboutSuperbet.AboutUs],
        links[AboutSuperbet.ResponsibleGaming],
        links[AboutSuperbet.PrivacyPolicy],
        links[AboutSuperbet.CookiePolicy],
        links[AboutSuperbet.TermsAndConditions],
        links[AboutSuperbet.ComplaintsProcedure],
        links[AboutSuperbet.Redaction],
        links[AboutSuperbet.Marketing],
    ],
    [Country.BrSocial]: [
        links[AboutSuperbet.PrivacyPolicy],
        links[AboutSuperbet.TermsAndConditions],
    ],
    [Country.Be]: [
        links[AboutSuperbet.Help],
        links[AboutSuperbet.AboutUsBelgium],
        links[AboutSuperbet.CareersBelgium],
        links[AboutSuperbet.ResponsibleGaming],
        links[AboutSuperbet.GrandCasinoKnokke],
        links[AboutSuperbet.GamingHalls],
        links[AboutSuperbet.TermsAndConditions],
        links[AboutSuperbet.PrivacyPolicy],
        links[AboutSuperbet.CookiePolicy],
    ],
    [Country.Bg]: [
        links[AboutSuperbet.ResponsibleGaming],
        links[AboutSuperbet.PrivacyPolicy],
        links[AboutSuperbet.CookiePolicy],
        links[AboutSuperbet.TermsAndConditions],
        links[AboutSuperbet.BettingAndGameRules],
    ],
}[getConfig().countrySpecific.country];

function getAllAboutSuperbetLinks(): Record<AboutSuperbet, AboutSuperbetLink> {
    return {
        [AboutSuperbet.AboutUs]: {
            label: 'about us',
            path: WikiPath.AboutUs,
            type: 'static',
            e2e: 'about-us',
        },
        [AboutSuperbet.AboutUsBelgium]: {
            label: 'about us',
            path: `https://corporate.napoleonsportsandcasino.be/${getConfig().appLocale.languageCode}`,
            type: 'external',
            e2e: 'about-us',
        },
        [AboutSuperbet.Agencies]: {
            label: 'agencies',
            routeName: ShopsRoutes.Shops,
            type: 'route',
            e2e: 'agencies',
            dynamic: true,
        },
        [AboutSuperbet.Help]: {
            label: 'help',
            path: WikiPath.Help,
            type: 'static',
            e2e: 'help',
        },
        [AboutSuperbet.DataProtection]: {
            label: 'data protection',
            path: WikiPath.DataProtection,
            type: 'static',
            e2e: 'data-protection',
        },
        [AboutSuperbet.CookiePolicy]: {
            label: 'cookie policy',
            path: WikiPath.CookiePolicy,
            type: 'static',
            e2e: 'cookie-policy',
        },
        [AboutSuperbet.ResponsibleGaming]: {
            label: 'responsible gaming',
            path: WikiPath.ResponsibleGaming,
            type: 'static',
            e2e: 'responsible-gaming',
        },
        [AboutSuperbet.AntiLaunderingPolicy]: {
            label: 'Anti-laundering money policy',
            path: WikiPath.AntiLaunderingPolicy,
            type: 'static',
            e2e: 'anti-laundering',
        },
        [AboutSuperbet.GameRules]: {
            label: 'game rules',
            path: WikiPath.GameRules,
            type: 'static',
            e2e: 'game-rules',
        },
        [AboutSuperbet.TermsAndConditions]: {
            label: 'Terms and Conditions',
            path: WikiPath.TermsAndConditions,
            type: 'static',
            e2e: 'terms-and-conditions',
        },
        [AboutSuperbet.Careers]: {
            label: 'careers',
            path: WikiPath.Careers,
            type: 'static',
            e2e: 'careers',
        },
        [AboutSuperbet.CareersBelgium]: {
            label: 'careers',
            path: `https://corporate.napoleonsportsandcasino.be/${getConfig().appLocale.languageCode}`,
            type: 'external',
            e2e: 'careers',
        },
        [AboutSuperbet.Cooperation]: {
            label: 'cooperation',
            path: WikiPath.Cooperation,
            type: 'static',
            e2e: 'cooperation',
        },
        [AboutSuperbet.PressOffice]: {
            label: 'press office',
            path: WikiPath.PressOffice,
            type: 'static',
            e2e: 'press-office',
        },
        [AboutSuperbet.PrivacyPolicy]: {
            label: 'wiki.privacyPolicy',
            path: WikiPath.PrivacyPolicy,
            type: 'static',
            e2e: 'privacy-policy',
        },
        [AboutSuperbet.AbcPlayer]: {
            label: 'ABC player',
            path: WikiPath.AbcPlayer,
            type: 'static',
            e2e: 'abc-player',
        },
        [AboutSuperbet.BettingRules]: {
            label: 'Betting Rules',
            path: WikiPath.BettingRules,
            type: 'static',
            e2e: 'betting-rules',
        },
        [AboutSuperbet.BettingAndGameRules]: {
            label: 'betting and game rules',
            path: WikiPath.BettingAndGameRules,
            type: 'static',
            e2e: 'betting-and-game-rules',
        },
        [AboutSuperbet.ComplaintsProcedure]: {
            label: 'complaints procedure',
            path: WikiPath.ComplaintsProcedure,
            type: 'static',
            e2e: 'complaints-procedure',
        },
        [AboutSuperbet.GrandCasinoKnokke]: {
            label: 'grand casino knokke',
            path: setLanguageToPath('https://www.grandcasinoknokke.be'),
            type: 'external',
            e2e: 'grand-casino-knokke',
        },
        [AboutSuperbet.GamingHalls]: {
            label: 'gaming halls',
            path: setLanguageToPath('https://www.powerofwinning.be'),
            type: 'external',
            e2e: 'gaming-halls',
        },
        [AboutSuperbet.License]: {
            label: 'footer.license',
            path: '/static/document/licenta-2024.pdf',
            type: 'external',
            e2e: 'license',
        },
        [AboutSuperbet.LicenseBrazil]: {
            label: 'footer.license',
            path: WikiPath.Licenses,
            type: 'static',
            e2e: 'license',
        },
        [AboutSuperbet.AuthorizationDecision]: {
            label: 'footer.authorizationDecision',
            path: '/static/document/decizie-autorizare-2025.pdf',
            type: 'external',
            e2e: 'authorization-decision',
        },
        [AboutSuperbet.Redaction]: {
            label: 'footer.redaction',
            path: 'mailto:redakcija@superbet.rs',
            type: 'external',
            e2e: 'redaction',
        },
        [AboutSuperbet.Marketing]: {
            label: 'footer.marketing',
            path: 'mailto:marketing@superbet.rs',
            type: 'external',
            e2e: 'marketing',
        },
        [AboutSuperbet.CustomerSupport]: {
            label: 'footer.customerSupport',
            path: WikiPath.CustomerSupport,
            type: 'static',
            e2e: 'customer-support',
        },
    };
}

function setLanguageToPath(host: string): string {
    const localizedPaths: Partial<Record<IsoLanguageCode, string>> = {
        [IsoLanguageCode.French]: 'fr',
        [IsoLanguageCode.Dutch]: 'nl',
        [IsoLanguageCode.England]: '',
    };
    return `${host}/${localizedPaths[getConfig().appLocale.languageCode] || ''}`;
}
